import React, { memo, useCallback, useEffect, useState } from "react";
import { useTranslate, transformDateToLocalDateString } from "helpers";
import { Button } from "ui/components/Button/Button";
import { ContactEditFormValues, ContactNoteRow } from "services/api/contacts/types";
import { getContactNotesAPI, deleteContactNoteAPI } from "services/api/contacts/api";
import { initialDataWithPagination } from "services/api/consts";
import { ReactSVG } from "react-svg";
import { Loader } from "ui/components/Loader";
import ContactNoteEditCard from "ui/modules/contacts/pages/ContactDetailPage/components/ContactDetailCard/components/ContactNoteEditCard/ContactNoteEditCard";
import { Badge } from "ui/components/Badge";

interface ContactNoteStepProps {
  formValues: ContactEditFormValues;
}

const ContactNoteStep: React.FC<ContactNoteStepProps> = ({ formValues: parentFormValues }) => {
  const contactId = parentFormValues.id;
  const { t } = useTranslate();
  const [notes, setNotes] = useState<ContactNoteRow[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [editNoteId, setEditNoteId] = useState<string | null>(null);

  const fetchNotes = useCallback(async () => {
    if (!contactId) return;
    setIsLoading(true);
    try {
      const result = await getContactNotesAPI(initialDataWithPagination.pagination, contactId);
      setNotes(result.data);
    } catch {
      setNotes([]);
    }
    setIsLoading(false);
  }, [contactId]);

  useEffect(() => {
    if (contactId) fetchNotes();
  }, [contactId]);

  const handleEdit = (noteId: string) => {
    setEditNoteId(noteId);
    setShowEdit(true);
  };

  const handleAddNew = () => {
    setEditNoteId(null);
    setShowEdit(true);
  };

  const handleDelete = async (noteId: string) => {
    if (!contactId) return;
    await deleteContactNoteAPI(t, contactId, noteId);
    fetchNotes();
  };

  const handleEditSuccess = () => {
    setShowEdit(false);
    setEditNoteId(null);
    fetchNotes();
  };

  return (
    <>
      <div className="flex items-center justify-center px-4 mt-8 xl:mt-16 min-h-[60vh] xl:min-h-[600px]">
        <div className="w-full max-w-4xl">
          <h1 className="text-2xl font-bold text-slate-900 text-center mb-1">Bilješke {parentFormValues.contactTypeId === "C" ? "tvrtke" : "osobe"}</h1>
          <p className="text-center text-sm text-slate-600 mb-6 max-w-2xl mx-auto ">
            Dodajte bilješke o kontaktu klikom na gumb <strong>"Dodaj bilješku"</strong>.
            Nakon unosa možete nastaviti na sljedeći korak.
          </p>

          {isLoading ? (
            <Loader isLoading={true}> </Loader>
          ) : (
            <div className="px-3">
              {notes.length === 0 ? (
                <div className="max-w-md mx-auto">
                  <Button
                    variant="primary"
                    size="full"
                    startIcon="/media/icons/plus.svg"
                    children="Dodaj bilješku"
                    onClick={handleAddNew}
                  />
                </div>
              ) : (
                <>
                  <div className="">
                    {notes.map((note) => (
                      <div key={note.id} className="flex items-start justify-between p-3 bg-slate-50 border border-slate-200 rounded mb-2 last:mb-0">
                        <div className="flex-1">
                          <p className="text-sm whitespace-pre-wrap flex items-center gap-2"><span>{note.content}</span>    {note.important && <Badge text="Važna bilješka" variant="warning" height="small" />}</p>
                          <div className="flex gap-2 mt-2 text-xs text-slate-400">
                            {note.userCreated && (
                              <span>{note.userCreated.firstName} {note.userCreated.lastName}</span>
                            )}
                            <span>{transformDateToLocalDateString(note.created ? new Date(note.created) : null)}</span>
                          </div>
                       
                        </div>
                        <div className="flex gap-1 ml-2">
                          <button type="button" onClick={() => handleEdit(note.id)} className="p-2 hover:bg-slate-200 rounded-full">
                            <ReactSVG src="/media/icons/edit.svg" className="w-4 h-4 stroke-slate-600" />
                          </button>
                          <button type="button" onClick={() => handleDelete(note.id)} className="p-2 hover:bg-red-100 rounded-full">
                            <ReactSVG src="/media/icons/trash.svg" className="w-4 h-4 stroke-red-600" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="flex justify-end mt-3">
                    <Button variant="primary" startIcon="/media/icons/plus.svg" children="Dodaj bilješku" onClick={handleAddNew} />
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </div>

      <ContactNoteEditCard
        contactId={contactId ?? ""}
        noteId={editNoteId}
        show={showEdit}
        onSuccess={handleEditSuccess}
        onClose={() => setShowEdit(false)}
        
      />
    </>
  );
};

export default memo(ContactNoteStep);
